/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Branding } from "../../model/branding";
import { BrandingUiInfoKeys } from "../../model/entities";
import { DeviceModel } from "../../model/device";
import { Locale, LocaleKeys } from "../../model/locale";
import type { AlertOverlayConfig } from "../alertOverlay";

/** Fallback phone number used when branding does not provide one. */
const FALLBACK_PHONE_NUMBER: string = "1-888 234-5678";

export function getCustomerSupportPhoneNumber(): string {
  const phoneNumber = Branding.getFallbackBrandingUiInfoForKey(
    BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER
  ) as string;

  if (phoneNumber !== undefined && phoneNumber !== null && phoneNumber !== "") {
    return phoneNumber;
  }

  return FALLBACK_PHONE_NUMBER;
}

/**
 * Build the localized Need Help message by substituting the
 * dynamic phone number, app version, OS version and device id
 * into the NEED_HELP_MESSAGE template.
 *
 * Lives in the viewModel layer because it reads Branding, DeviceModel and
 * Locale — all model-layer services the view layer must not touch directly.
 * Both `SignInController` (D-pad NAV_OK on the Need Help link) and
 * `UserIdPasswordScreen` (pointer click) consume it through
 * `viewModel/signIn`, so the message format has a single source of truth.
 */
export function buildNeedHelpMessage(): string {
  const phoneNumber: string = getCustomerSupportPhoneNumber();

  // Locale templates use double-brace placeholders ({{phoneNumber}}); matching
  // only the inner single-brace form left a stray "{…}" around each value.
  return Locale.getString(LocaleKeys.NEED_HELP_MESSAGE)
    .replace("{{phoneNumber}}", phoneNumber)
    .replace("{{appVersion}}", DeviceModel.getAppVersionName())
    .replace(
      "{{osVersion}}",
      DeviceModel.getTVProperties().getPlatformVersion()
    )
    .replace("{{deviceId}}", DeviceModel.getDeviceUuid());
}

/**
 * Build the full payload for the Need Help alert overlay.
 * Returned object is suitable for dispatching via
 * `showAlertOverlay(...)`.
 */
export function getNeedHelpAlertConfig(): AlertOverlayConfig {
  return {
    title: Locale.getString(LocaleKeys.NEED_HELP_TEXT),
    message: buildNeedHelpMessage(),
    variant: "info",
    actions: [
      {
        id: "close",
        label: Locale.getString(LocaleKeys.ALERT_ACTION_CLOSE)
      }
    ]
  };
}
